import {utils} from '@wallaby-ui/core';
import {cloneDeep, findIndex, get, isEmpty, isEqual, omit, set} from 'lodash';
import {AnyAction} from 'redux';

import * as at from './auto_layout_action_types';
import AutoLayoutService from 'src/js/lib/auto_layout/auto_layout_service';
import {
  KEYWORD_DATA_CONTENT,
  KEYWORD_DATA_ITEMS,
} from 'src/js/model/auto_layout_screen_data_helpers';
import {MiniScreensConfig} from 'src/js/model/mini_screens_config';

export const screenPropertiesDefaultState = {
  isFromDb: false,
  useDefaultCollapse: true,
  isCollapsed: false,
  isEditing: false,
  isInvalid: false,
  invalidByDelete: false,
  isLoading: false,
  isRemoving: false,
  isSaving: false,
};

const defaultState = {
  conflictLastChangeAuthor: {},
  currentContextParams: undefined,
  dataChanges: {},
  dataCache: {},
  errors: {},
  indexCheckings: {},
  isLoadingCounter: 0,
  loadedDataCache: {},
  metadataCache: {},
  miniScreensConfig: new MiniScreensConfig(),
  screenProperties: {},
  selectedScreenIds: {},
  validation: {},
};

const STATIC_EMPTY_OBJECT = {};
const ERROR_TYPE = {
  LOAD: 'LOAD',
  REMOVE: 'REMOVE',
  SAVE: 'SAVE',
};

function changeScreenProperty(screenId, state, dataChange) {
  return {
    ...state,
    screenProperties: {
      ...state.screenProperties,
      [screenId]: {
        ...state.screenProperties[screenId],
        ...dataChange,
      },
    },
  };
}

function deleteIndexChange(state, screenId, itemId) {
  let newState = {};
  if (state.dataChanges) {
    if (
      state.dataChanges[screenId] &&
      state.dataChanges[screenId][KEYWORD_DATA_ITEMS]
    ) {
      const itemScreenId = AutoLayoutService.getItemScreenId(
        AutoLayoutService.getCoreScreenId(screenId),
        itemId
      );
      const itemIdx = findIndex(
        state.dataChanges[screenId][KEYWORD_DATA_ITEMS],
        (item) => item === itemId
      );
      const dataChangesWithoutItem = [
        ...state.dataChanges[screenId][KEYWORD_DATA_ITEMS].slice(0, itemIdx),
        ...state.dataChanges[screenId][KEYWORD_DATA_ITEMS].slice(itemIdx + 1),
      ];
      if (isEmpty(dataChangesWithoutItem)) {
        // removed last change of screen, remove whole screen reference
        newState = omit(state.dataChanges, [screenId, itemScreenId]);
      } else {
        newState = {
          ...omit(state.dataChanges, [itemScreenId]),
          [screenId]: {
            ...state.dataChanges[screenId],
            [KEYWORD_DATA_ITEMS]: dataChangesWithoutItem,
          },
        };
      }
    } else {
      newState = {...state.dataChanges};
    }
  }
  return newState;
}

function updateOrDeleteDataChange(
  state,
  screenId,
  pathToData,
  loadedData,
  newData
) {
  const isDelete = isEqual(loadedData, newData);
  let newState = {};
  if (isDelete && isEmpty(omit(state.dataChanges[screenId], [pathToData]))) {
    // removed last change of screen, remove whole screen reference
    newState = omit(state.dataChanges, [screenId]);
  } else {
    newState = {
      ...state.dataChanges,
      [screenId]: isDelete
        ? omit(state.dataChanges[screenId], [pathToData])
        : {
            ...state.dataChanges[screenId],
            [pathToData]: newData,
          },
    };
  }
  return newState;
}

function updateDataCache(stateDataCache, screenId, itemId) {
  const itemIdx = findIndex(
    stateDataCache[screenId][KEYWORD_DATA_ITEMS],
    (item) => item === itemId
  );
  let newDataCache = {...stateDataCache};
  if (itemIdx !== -1) {
    // removing item
    newDataCache = {
      ...stateDataCache,
      [screenId]: {
        ...stateDataCache[screenId],
        [KEYWORD_DATA_ITEMS]: [
          ...stateDataCache[screenId][KEYWORD_DATA_ITEMS].slice(0, itemIdx),
          ...stateDataCache[screenId][KEYWORD_DATA_ITEMS].slice(itemIdx + 1),
        ],
      },
    };
  } else {
    // adding item
    newDataCache = {
      ...stateDataCache,
      [screenId]: {
        ...stateDataCache[screenId],
        [KEYWORD_DATA_ITEMS]: [
          ...stateDataCache[screenId][KEYWORD_DATA_ITEMS],
          itemId,
        ],
      },
    };
  }
  return newDataCache;
}

function reducer(state = defaultState, action: AnyAction) {
  switch (action.type) {
    case at.SET_CURRENT_CONTEXT_PARAMS: {
      return {...state, currentContextParams: action.payload.contextParams};
    }
    case at.AUTO_LAYOUT_LOAD_START: {
      return {...state, isLoadingCounter: state.isLoadingCounter + 1};
    }
    case at.AUTO_LAYOUT_LOAD_END: {
      return {
        ...state,
        isLoadingCounter: Math.max(state.isLoadingCounter - 1, 0),
      };
    }
    case at.AUTO_LAYOUT_INIT_DATA: {
      const {screenId, requestScreenId, response, content, reload} =
        action.payload;
      let screenData = {...response, screenId: requestScreenId};
      if (content) {
        // add content for empty data
        screenData = {...screenData, ...content};
      }
      return {
        ...state,
        // remember changes unless index is reloaded
        dataCache: reload
          ? {...state.dataCache, [screenId]: screenData}
          : {[screenId]: screenData, ...state.dataCache},
        loadedDataCache: {
          ...state.loadedDataCache,
          [screenId]: cloneDeep(screenData),
        },
      };
    }
    case at.AUTO_LAYOUT_INIT_SCREEN_PROPERTIES: {
      const {screenId, screenProperties} = action.payload;
      return {
        ...state,
        // remember changes
        screenProperties: {
          ...state.screenProperties,
          [screenId]: {...screenPropertiesDefaultState, ...screenProperties},
        },
      };
    }
    case at.AUTO_LAYOUT_FETCH_METADATA_SUCCESS: {
      const {screenId, response} = action.payload;
      return {
        ...state,
        metadataCache: {...state.metadataCache, [screenId]: response},
        screenProperties: {
          ...state.screenProperties,
          [screenId]: {
            ...screenPropertiesDefaultState,
          },
        },
      };
    }
    case at.AUTO_LAYOUT_CHANGE_DATA: {
      const {screenId, dataChange} = action.payload;
      const newData = set(
        {...state.dataCache[screenId][KEYWORD_DATA_CONTENT]},
        dataChange.pathToData,
        dataChange.newData
      );
      const loadedData = get(
        state.loadedDataCache[screenId][KEYWORD_DATA_CONTENT],
        dataChange.pathToData
      );
      let updatedValidation = {...state.validation};
      // clear validation result of current field
      if (state.validation[screenId]) {
        updatedValidation = {
          [screenId]: {
            errors: omit(state.validation[screenId].errors, [
              dataChange.pathToData,
            ]),
            messages: omit(state.validation[screenId].messages, [
              dataChange.pathToData,
            ]),
          },
        };
      }
      return {
        ...state,
        dataCache: {
          ...state.dataCache,
          [screenId]: {
            ...state.dataCache[screenId],
            [KEYWORD_DATA_CONTENT]: newData,
          },
        },
        dataChanges: updateOrDeleteDataChange(
          state,
          screenId,
          dataChange.pathToData,
          loadedData,
          dataChange.newData
        ),
        validation: updatedValidation,
      };
    }
    case at.AUTO_LAYOUT_CHECKED_INDEX_STATE: {
      const {
        screenId,
        itemsDifferences,
        currentIndexItemsDifferences,
        invalidIndexError,
        invokeInfo,
      } = action.payload;
      return {
        ...state,
        indexCheckings: {
          ...state.indexCheckings,
          [screenId]: {
            itemsDifferences,
            currentIndexItemsDifferences,
            invalidIndexError,
            invokeInfo,
          },
        },
      };
    }
    case at.AUTO_LAYOUT_CHECKED_INDEX_STATE_RESET: {
      const {screenId} = action.payload;
      return {
        ...state,
        indexCheckings: omit(state.indexCheckings, [screenId]),
      };
    }
    case at.AUTO_LAYOUT_CHANGE_SCREEN_PROPERTY: {
      const {screenId, propertyChange} = action.payload;
      return changeScreenProperty(screenId, state, propertyChange);
    }
    case at.AUTO_LAYOUT_FETCH_DATA: {
      const {screenId} = action.payload;
      return changeScreenProperty(screenId, state, {isLoading: true});
    }
    case at.AUTO_LAYOUT_FETCH_DATA_SUCCESS: {
      const {screenId} = action.payload;
      return {
        ...changeScreenProperty(screenId, state, {
          isInvalid: false,
          invalidByDelete: false,
          isLoading: false,
        }),
        // remove all errors on reload
        errors: {...state.errors, [screenId]: STATIC_EMPTY_OBJECT},
      };
    }
    case at.AUTO_LAYOUT_FETCH_DATA_FAILURE: {
      const {screenId, errorMessage} = action.payload;
      return {
        ...changeScreenProperty(screenId, state, {isLoading: false}),
        errors: {
          ...state.errors,
          [screenId]: {
            ...state.errors[screenId],
            [ERROR_TYPE.LOAD]: errorMessage,
          },
        },
      };
    }
    case at.AUTO_LAYOUT_DATA_VALIDATED: {
      const {screenId, errors, messages, warnings} = action.payload;
      return {
        ...state,
        validation: {
          [screenId]: {errors, messages, warnings},
        },
      };
    }
    case at.AUTO_LAYOUT_SAVE_DATA: {
      const {screenId} = action.payload;
      return changeScreenProperty(screenId, state, {isSaving: true});
    }
    case at.AUTO_LAYOUT_SAVE_DATA_SUCCESS: {
      const {screenId, data} = action.payload;
      const newState = changeScreenProperty(screenId, state, {
        isSaving: false,
        isFromDb: true,
      });
      return {
        ...newState,
        dataCache: {...state.dataCache, [screenId]: data},
        loadedDataCache: {
          ...state.loadedDataCache,
          [screenId]: cloneDeep(data),
        },
        dataChanges: omit(state.dataChanges, [screenId]),
        errors: {
          ...state.errors,
          [screenId]: omit(state.errors[screenId], [ERROR_TYPE.SAVE]),
        },
        validation: omit(state.validation, [screenId]),
      };
    }
    case at.AUTO_LAYOUT_SAVE_DATA_FAILURE: {
      const {screenId, isInvalid, errorMessage} = action.payload;
      return {
        ...changeScreenProperty(screenId, state, {
          isSaving: false,
          ...(isInvalid ? {isInvalid} : {}),
        }),
        ...(!isInvalid
          ? {
              errors: {
                ...state.errors,
                [screenId]: {
                  ...state.errors[screenId],
                  [ERROR_TYPE.SAVE]: errorMessage,
                },
              },
            }
          : {}),
      };
    }
    case at.AUTO_LAYOUT_DELETE_DATA: {
      const {screenId} = action.payload;
      return changeScreenProperty(screenId, state, {isRemoving: true});
    }
    case at.AUTO_LAYOUT_DELETE_DATA_SUCCESS: {
      const {screenId} = action.payload;
      const newState = changeScreenProperty(screenId, state, {
        isRemoving: false,
      });
      return {
        ...newState,
        conflictLastChangeAuthor: omit(state.conflictLastChangeAuthor, [
          screenId,
        ]),
        dataCache: utils.stateUtils.deleteProperty(state.dataCache, screenId),
        indexCheckings: omit(state.indexCheckings, [screenId]),
        loadedDataCache: utils.stateUtils.deleteProperty(
          state.loadedDataCache,
          screenId
        ),
        dataChanges: omit(state.dataChanges, [screenId]),
        screenProperties: utils.stateUtils.deleteProperty(
          state.screenProperties,
          screenId
        ),
        errors: {
          ...state.errors,
          [screenId]: omit(state.errors[screenId], [ERROR_TYPE.REMOVE]),
        },
        validation: omit(state.validation, [screenId]),
      };
    }
    case at.AUTO_LAYOUT_DATA_REMOVED: {
      const {screenId} = action.payload;
      return {
        ...state,
        conflictLastChangeAuthor: omit(state.conflictLastChangeAuthor, [
          screenId,
        ]),
        dataCache: utils.stateUtils.deleteProperty(state.dataCache, screenId),
        indexCheckings: omit(state.indexCheckings, [screenId]),
        loadedDataCache: utils.stateUtils.deleteProperty(
          state.loadedDataCache,
          screenId
        ),
        dataChanges: omit(state.dataChanges, [screenId]),
        screenProperties: utils.stateUtils.deleteProperty(
          state.screenProperties,
          screenId
        ),
        errors: {
          ...state.errors,
          [screenId]: omit(state.errors[screenId], [ERROR_TYPE.REMOVE]),
        },
        validation: omit(state.validation, [screenId]),
      };
    }
    case at.AUTO_LAYOUT_DELETE_DATA_FAILURE: {
      const {screenId, isInvalid, errorMessage} = action.payload;
      return {
        ...changeScreenProperty(screenId, state, {
          isRemoving: false,
          ...(isInvalid ? {isInvalid, invalidByDelete: true} : {}),
        }),
        ...(!isInvalid
          ? {
              errors: {
                ...state.errors,
                [screenId]: {
                  ...state.errors[screenId],
                  [ERROR_TYPE.REMOVE]: errorMessage,
                },
              },
            }
          : {}),
      };
    }
    case at.AUTO_LAYOUT_INDEX_ADD: {
      const {screenId, itemId} = action.payload;
      const newDataCache = updateDataCache(state.dataCache, screenId, itemId);
      return {
        ...state,
        dataCache: newDataCache,
        dataChanges: {
          ...state.dataChanges,
          [screenId]: {
            ...state.dataChanges[screenId],
            [KEYWORD_DATA_ITEMS]: [
              ...(state.dataChanges[screenId]
                ? state.dataChanges[screenId][KEYWORD_DATA_ITEMS]
                : []),
              itemId,
            ],
          },
        },
      };
    }
    case at.AUTO_LAYOUT_INDEX_REMOVE: {
      const {screenId, itemId} = action.payload;
      const newDataCache = updateDataCache(state.dataCache, screenId, itemId);
      return {
        ...state,
        dataCache: newDataCache,
        dataChanges: deleteIndexChange(state, screenId, itemId),
      };
    }
    case at.SET_MINI_SCREENS_CONFIG: {
      const {config, reload} = action.payload;
      if (reload || (config && !state.miniScreensConfig.isLoaded())) {
        return {...state, miniScreensConfig: config};
      }
      break;
    }
    case at.CHANGE_SELECTED_SCREEN_ID: {
      const {screenId, contextParamsId} = action.payload;
      return {
        ...state,
        selectedScreenIds: {
          ...state.selectedScreenIds,
          [contextParamsId]: screenId,
        },
      };
    }
    case at.AUTO_LAYOUT_RESET_CHANGES: {
      const {screenId} = action.payload;
      return {
        ...state,
        dataCache: {
          ...state.dataCache,
          [screenId]: cloneDeep(state.loadedDataCache[screenId]),
        },
        dataChanges: omit(state.dataChanges, [screenId]),
        validation: omit(state.validation, [screenId]),
      };
    }
    case at.AUTO_LAYOUT_CONFLICT_LAST_CHANGE_AUTHOR: {
      const {lastChangeAuthor, screenId} = action.payload;
      return {
        ...state,
        conflictLastChangeAuthor: {
          ...state.conflictLastChangeAuthor,
          [screenId]: lastChangeAuthor,
        },
      };
    }
    case at.AUTO_LAYOUT_CONFLICT_LAST_CHANGE_AUTHOR_RESET: {
      const {screenId} = action.payload;
      return {
        ...state,
        conflictLastChangeAuthor: omit(state.conflictLastChangeAuthor, [
          screenId,
        ]),
      };
    }
  }
  return state;
}

export default reducer;
